import { NextRequest, NextResponse } from 'next/server';

import { ROLES, RoleType } from 'types/roles';

/**
 * Removes port number from hostname for production redirects
 * In production, we don't want port numbers in redirect URLs
 */
export function cleanHostnameForRedirect(hostname: string): string {
  // In production or staging environments (not localhost/development), strip port numbers
  const env = (process.env.NODE_ENV ?? '');
  const isDevelopment = env !== 'production';
  const isLocalhost = hostname.includes('localhost') || hostname.includes('127.0.0.1');
  
  if (!isDevelopment && !isLocalhost) {
    // Remove port number by splitting on ':' and taking the first part
    return hostname.split(':')[0] || hostname;
  }
  
  return hostname;
}

/**
 * Helper function to handle subdomain-aware redirects according to these rules:
 * 1. If subdomain is empty, return null (ignore subdomain rules)
 * 2. If valid subdomain is not empty and user is logged in: ensure subdomain equals RoleType in pathname
 * 3. If valid subdomain is not empty and user is not logged in: redirect RoleType in pathname to subdomain role
 * 4. If subdomain and RoleType in pathname are equal, return null (no redirect needed)
 */
export function getSubdomainAwareRedirect(params: {
  request: NextRequest,
  subdomain: string,
  isAuthenticated: boolean,
  pathRole?: RoleType,
  userRole?: RoleType,
  customUrl?: URL,
  hostname?: string
}): NextResponse | null {
  const { request, subdomain, isAuthenticated, pathRole, userRole, customUrl, hostname } = params;
  const url = request.nextUrl;
  // Rule 1: If subdomain is empty, ignore subdomain rules
  if (!subdomain) {
    return null;
  }

  // Check if subdomain is a valid role
  if (!isValidSubdomainRole(subdomain)) {
    return null;
  }

  // Rule 4: If subdomain and pathRole are equal, no redirect needed
  if (pathRole && subdomain === pathRole) {
    return null;
  }
  
  let redirectUrl = customUrl ? new URL(customUrl) : new URL(url);

  // Use the correct hostname from the request headers if provided
  // Clean hostname to remove port numbers in production
  if (hostname && redirectUrl) {
    redirectUrl = new URL(redirectUrl.toString().replace(redirectUrl.host, cleanHostnameForRedirect(hostname)));
  }

  if (isAuthenticated && userRole) {
    // Rule 2: User is logged in - ensure subdomain equals user's actual role
    if (subdomain !== userRole) {
      // Redirect to make subdomain match user's role
      const rewrittenPath = rewritePathWithSubdomain(redirectUrl.pathname, userRole);
      if (rewrittenPath) {
        redirectUrl.pathname = rewrittenPath;
        return NextResponse.redirect(redirectUrl);
      }
    }
  } else {
    // Rule 3: User is not logged in - redirect RoleType in pathname to subdomain role
    if (pathRole && subdomain !== pathRole) {
      const rewrittenPath = rewritePathWithSubdomain(redirectUrl.pathname, subdomain);
      if (rewrittenPath) {
        redirectUrl.pathname = rewrittenPath;
        return NextResponse.redirect(redirectUrl);
      }
    }
  }

  return null;
}

export function rewritePathWithSubdomain(path: string, subdomain: string): string | false {
  for (const role of Object.values(ROLES)) {
    if (path.includes(`/${role}`) && role !== subdomain) {
      return path.replace(`/${role}`, `/${subdomain}`);
    }
  }
  return false;
}

export function isValidSubdomainRole(subdomain: string): subdomain is RoleType {
  const validRoles = Object.values(ROLES) as string[];
  return validRoles.includes(subdomain);
}